import {type ActionSheetButton, useIonActionSheet} from "@ionic/react";
import {useTranslation} from "react-i18next";
import {useCallback} from "react";
import {
  closeOutline,
  pencilOutline,
  printOutline,
  shareSocialOutline,
  swapHorizontalOutline,
  trashOutline
} from "ionicons/icons";
import {TicketStatus} from "app/gql/graphqlSchema";
import {ticketStatusIcon} from "app/employee/ticket/ticketStatusIcon";
import {UnreachableCaseError} from "ts-essentials";

interface TicketActionsProps {
  currentStatus: TicketStatus;
  onEdit?: () => void;
  onDelete?: () => void;
  onChangeStatus?: (status: TicketStatus) => void;
  onPrint?: () => void | Promise<void>;
  onShare?: () => void | Promise<void>;
}

export const useTicketActions = ({
  currentStatus,
  onEdit,
  onDelete,
  onChangeStatus,
  onPrint,
  onShare
}: TicketActionsProps) => {
  const {t} = useTranslation();
  const [presentActionSheet] = useIonActionSheet();
  const [presentStatusSheet] = useIonActionSheet();

  const showStatusActions = useCallback(async () => {
    if (!onChangeStatus) return;
    const inProgressButton = {
      text: t("ticket.action.accept"),
      icon: ticketStatusIcon[TicketStatus.InProgress],
      handler: () => onChangeStatus(TicketStatus.InProgress)
    } satisfies ActionSheetButton;

    const todoButton = {
      text: t("ticket.action.todo"),
      icon: ticketStatusIcon[TicketStatus.Todo],
      handler: () => onChangeStatus(TicketStatus.Todo)
    } satisfies ActionSheetButton;

    const doneButton = {
      text: t("ticket.action.done"),
      icon: ticketStatusIcon[TicketStatus.Done],
      handler: () => onChangeStatus(TicketStatus.Done)
    } satisfies ActionSheetButton;

    const buttons: ActionSheetButton[] = [];
    if (currentStatus === TicketStatus.InProgress) {
      buttons.push(doneButton, todoButton);
    } else if (currentStatus === TicketStatus.Todo) {
      buttons.push(inProgressButton, doneButton);
    } else if (currentStatus === TicketStatus.Done) {
      buttons.push(inProgressButton, todoButton);
    } else {
      throw new UnreachableCaseError(currentStatus);
    }
    buttons.push({text: t("g.close"), icon: closeOutline, role: "cancel"});

    await presentStatusSheet({
      header: t("ticket.change-status"),
      buttons
    });
  }, [currentStatus, onChangeStatus, presentStatusSheet, t]);

  const showActions = useCallback(async () => {
    const buttons: ActionSheetButton[] = [];
    if (onChangeStatus) {
      buttons.push({
        text: t("ticket.change-status"),
        icon: swapHorizontalOutline,
        handler: () => {
          showStatusActions();
        }
      });
    }
    if (onEdit) {
      buttons.push({
        text: t("g.edit"),
        icon: pencilOutline,
        handler: onEdit
      });
    }
    if (onDelete) {
      buttons.push({
        text: t("g.delete"),
        icon: trashOutline,
        role: "destructive",
        handler: onDelete
      });
    }
    if (onPrint) {
      buttons.push({
        text: t("ticket.action.print"),
        icon: printOutline,
        handler: onPrint
      });
    }
    if (onShare) {
      buttons.push({
        text: t("g.share.button"),
        icon: shareSocialOutline,
        handler: onShare
      });
    }
    buttons.push({
      text: t("g.close"),
      icon: closeOutline,
      role: "cancel"
    });
    await presentActionSheet({
      header: t("g.action"),
      buttons
    });
  }, [onChangeStatus, onEdit, onDelete, onPrint, onShare, presentActionSheet, showStatusActions, t]);

  return {showActions, showStatusActions};
};
